import { getNode } from '@formkit/core';
import {
  postcodeValidator,
  postcodeValidatorExistsForCountry,
} from 'postcode-validator';
import * as countries from 'i18n-iso-countries';
import i18next from './i18n';

import validationParser from './validation-parser.js';
import { assert } from './assertions';
import {
  isValidPhoneNumber,
  parsePhoneNumberFromString,
} from 'libphonenumber-js/min';
import { humanizeList } from './humanizeList';
import { TAX_ID_VALIDATIONS, getTaxIdLabel } from './user-helpers';

export function errorHandler(error) {
  let formErrors = [];
  let inputErrors = {};

  switch (error.response?.status) {
    case 400: // validation error
    case 404: // not found - used for descriptive "no result found" errors
    case 409: // conflict
    case 422: // unprocessable entity - used as secondary validation error
    case 412: // precondition failed - used as secondary validation error
      inputErrors = validationParser('body', error.response.data.errors);
      formErrors = [error.response.data.message];
      break;

    default:
      formErrors = [i18next.t('common:errors.genericRequestFail')];
  }

  return {
    formErrors,
    inputErrors,
  };
}

export function handleErrors(formId, error) {
  const formNode = getNode(formId);
  if (!formNode) return;

  const { formErrors, inputErrors } = errorHandler(error);
  formNode.setErrors(formErrors, inputErrors);
}

export function postalCodeValidation(node, country) {
  const countryCode = (
    country ||
    node.at('country')?.value ||
    ''
  ).toUpperCase();

  return postcodeValidatorExistsForCountry(countryCode)
    ? postcodeValidator(String(node.value).toUpperCase(), countryCode)
    : true;
}

export function getPostcodeValidationMessage({ node, args }) {
  const countryCode = (
    args[0] ||
    node.at('country')?.value ||
    ''
  ).toUpperCase();
  const countryName = countries.getName(countryCode, i18next.resolvedLanguage, {
    select: 'official',
  });
  return i18next.t('formulate:validation.postcode', {
    countryName,
  });
}

export function taxIdFormatValidation(node, country) {
  if (!node.value) return true;
  const pattern = TAX_ID_VALIDATIONS[(country || '').toUpperCase()];
  return pattern
    ? new RegExp(pattern).test(String(node.value).toUpperCase())
    : true;
}

export function getTaxIdFormatValidationMessage({ args }) {
  const countryCode = (args[0] || '').toUpperCase();
  return i18next.t('formulate:validation.taxIdFormat', {
    label: getTaxIdLabel(countryCode) || 'tax ID',
    countryName: countries.getName(countryCode, i18next.resolvedLanguage, {
      select: 'official',
    }),
  });
}

export function phoneNumberValidation(node) {
  return isValidPhoneNumber(String(node.value) || '');
}

export function getPhoneNumberValidationMessage({ node }) {
  const parsed = parsePhoneNumberFromString(node.value);

  // Generic invalid phone number message
  if (!parsed) {
    return i18next.t('formulate:validation.phone');
  }

  const possibleCountries = parsed.getPossibleCountries();

  // If we can't determine any possible countries, we give a generic message
  if (!possibleCountries.length) {
    return i18next.t('formulate:validation.phoneUnknownCountry');
  }

  const countryNames = humanizeList(
    possibleCountries.map((code) =>
      countries.getName(code, i18next.resolvedLanguage, {
        select: 'official',
      })
    ),
    { conjunction: i18next.t('common:conjunction.or') }
  );

  // Return message with possible country names and country code
  return i18next.t('formulate:validation.phoneWithCountryName', {
    countryNames,
    countryCode: parsed.countryCallingCode,
  });
}

export const getNaturalOrderValidationMessage = {
  naturallyBefore: ({ node, args }) =>
    i18next.t('formulate:validation.naturallyBefore', {
      value: node.at(args[0])?.value,
    }),
  naturallyAfter: ({ node, args }) =>
    i18next.t('formulate:validation.naturallyAfter', {
      value: node.at(args[0])?.value,
    }),
};

export const naturallyBefore = (node, prop) => {
  if (!prop) {
    throw new Error('Missing prop for naturallyBefore');
  }
  const compareValue = node.at(prop)?.value;
  return compareValue == null ? true : node.value < compareValue;
};

export const naturallyAfter = (node, prop) => {
  if (!prop) {
    throw new Error('Missing prop for naturallyAfter');
  }
  const compareValue = node.at(prop)?.value;
  return compareValue == null ? true : node.value > compareValue;
};

export const serializeFormData = (data, schema) => {
  assert(data, 'data is required');
  assert(Array.isArray(schema), 'schema is required');
  return schema.reduce((acc, { name, $formkit: type, children }) => {
    if (type === 'group' && data[name]) {
      acc[name] = serializeFormData(data[name], children);
    } else {
      acc[name] = data[name];
    }
    return acc;
  }, {});
};

export const getFileInputSectionsSchema = () => ({
  noFiles: {
    children: [
      {
        $el: 'span',
        attrs: { 'data-i': 'upload_file', class: 'formkit-file-icon' },
      },
      ' ',
      i18next.t('common:forms.file.noFileChosen'),
    ],
  },
});

export const exportedForTesting = {
  naturallyBefore,
  naturallyAfter,
};
